/**
 * Shared event card: time, title, venue, price, optional image.
 * Used in Schedule (agenda) and Worship (list of events).
 */

import { Link } from "react-router-dom";
import { IconClock, IconMapPin, IconCheck, IconHeartFilled } from "./Icons.tsx";
import { formatTimeBangkok } from "../lib/bangkok-time.ts";
import { getCurrencySymbol, getMinPrice } from "../lib/format.ts";
import type { Event } from "../lib/graphql.ts";

function rsvpStatusLabel(status: string): string {
  switch (status) {
    case "GOING":
      return "Иду";
    case "INTERESTED":
      return "Интересуюсь";
    case "NOT_GOING":
      return "Не иду";
    default:
      return "Записан";
  }
}

export interface EventCardProps {
  event: Event;
  compact?: boolean;
  /** Скрыть заголовок (для списка богослужений, где все — «Богослужение») */
  hideTitle?: boolean;
  /** State для навигации (напр. { from: "/home-group/123" } для возврата в группу) */
  linkState?: Record<string, unknown>;
}

/** Event card for schedule agenda and worship list (same UX). */
export function EventCard({
  event,
  compact = false,
  hideTitle = false,
  linkState,
}: EventCardProps) {
  const { price, currency } = getMinPrice(event);
  const symbol = getCurrencySymbol(currency);
  const startTime = formatTimeBangkok(event.startsAt);
  const hasRsvp = !!event.myRsvp || !!event.hasMyTickets;
  const categoryColor =
    event.categories?.[0]?.color ?? undefined;

  return (
    <Link
      to={`/event/${event.id}`}
      state={linkState}
      className={`
        block rounded-xl lg-mobile:rounded-2xl overflow-hidden bg-white/95 
        border shadow-sm transition-all active:scale-[0.99]
        ${hasRsvp ? "border-orange-200" : "border-slate-100"}
        ${compact ? "" : ""}
      `}
    >
      <div className="flex">
        <div
          className="flex-shrink-0 w-1.5 rounded-l-xl lg-mobile:rounded-l-2xl"
          style={{
            backgroundColor: categoryColor ?? "rgb(203 213 225 / 0.6)",
          }}
          aria-hidden
        />
        <div
          className={`
            relative flex-shrink-0 w-16 xs:w-20 lg-mobile:w-24 flex flex-col items-center justify-center gap-1 py-3 xs:py-4 lg-mobile:py-5 border-r border-slate-100 overflow-hidden
            ${hasRsvp ? "bg-orange-50/30" : "bg-slate-50/80"}
          `}
        >
          {hasRsvp && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-hidden
            >
              <IconHeartFilled className="w-16 h-16 xs:w-20 xs:h-20 lg-mobile:w-24 lg-mobile:h-24 text-orange-300/40" />
            </div>
          )}
          <IconClock className="relative w-4 h-4 xs:w-5 xs:h-5 lg-mobile:w-6 lg-mobile:h-6 text-slate-400" />
          <span className="relative text-sm xs:text-base lg-mobile:text-lg font-semibold text-slate-700">
            {startTime}
          </span>
        </div>

        <div className="flex-1 min-w-0 p-3 xs:p-4 lg-mobile:p-5">
          {(!hideTitle || hasRsvp) && (
            <div className="flex items-start gap-2 mb-1 lg-mobile:mb-2">
              {!hideTitle && (
                <h3 className="text-sm xs:text-base lg-mobile:text-lg font-semibold text-slate-900 line-clamp-2 flex-1 min-w-0">
                  {event.title}
                </h3>
              )}
              {hasRsvp && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <IconCheck className="w-3.5 h-3.5" />
                  {event.myRsvp
                    ? rsvpStatusLabel(event.myRsvp.status)
                    : "Билет"}
                </span>
              )}
            </div>
          )}

          {event.venue && (
            <p className="text-xs xs:text-sm lg-mobile:text-base text-slate-500 flex items-center gap-1 lg-mobile:gap-1.5 mb-2 lg-mobile:mb-3">
              <IconMapPin className="w-3.5 h-3.5 xs:w-4 xs:h-4 lg-mobile:w-5 lg-mobile:h-5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{event.venue}</span>
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs xs:text-sm lg-mobile:text-base font-medium text-[var(--color-accent)]">
              {price !== null
                ? price === 0
                  ? "Бесплатно"
                  : `от ${price} ${symbol}`
                : ""}
            </span>
            <span className="text-xs xs:text-sm lg-mobile:text-base text-slate-400">
              Подробнее →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
